import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { EtudiantTabs, EtudiantForm, CalendrierTab, EmploiDuTempsTab, ActivitesTab, PlanningTab } from '../components'
import { useEtudiant, useCreateEtudiant, useUpdateEtudiant } from '../hooks/use-etudiants'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { EntityPageHeader } from '@/shared/components'
import type { CreateEtudiantRequest, UpdateEtudiantRequest } from '@/shared/types/etudiant'

export function EtudiantDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user } = useAuth()
  const isEdit = !!id

  // Admin cannot access student detail — redirect to list
  useEffect(() => {
    if (user?.role === 'admin') {
      navigate('/etudiants', { replace: true })
    }
  }, [user?.role, navigate])

  const { data: etudiant, isLoading } = useEtudiant(id ? parseInt(id) : undefined)
  const createEtudiant = useCreateEtudiant()
  const updateEtudiant = useUpdateEtudiant()

  const [activeTab, setActiveTab] = useState('identity')
  const [calendarLabel, setCalendarLabel] = useState<string>()

  const handleSubmit = (data: Record<string, unknown>, photoFile?: File, photoRemoved?: boolean) => {
    if (isEdit) {
      const payload: UpdateEtudiantRequest = {
        prenom: data.prenom as string,
        nom: data.nom as string,
        telephone: (data.telephone as string) || undefined,
        consentementSilencieux: data.consentementSilencieux as boolean,
        consentementConsultation: data.consentementConsultation as boolean,
        consentementModificationPlanning: data.consentementModificationPlanning as boolean,
        nbMotsLangue: data.nbMotsLangue as number,
        heureNotificationScan: data.heureNotificationScan as number,
        nbJoursAvantVacancesNotif: data.nbJoursAvantVacancesNotif as number,
        typeMemoire: (data.typeMemoire as string) || undefined,
        formateurId: data.formateurId as number | undefined,
      }

      const formData = new FormData()
      formData.append(
        'etudiant',
        new Blob([JSON.stringify(payload)], { type: 'application/json' }),
      )
      if (photoFile) {
        formData.append('file', photoFile)
      }
      if (photoRemoved) {
        formData.append('removePhoto', new Blob(['true'], { type: 'application/json' }))
      }

      updateEtudiant.mutate({ id: parseInt(id!), formData }, {
        onSuccess: () => window.location.reload(),
      })
    } else {
      const payload: CreateEtudiantRequest = {
        prenom: data.prenom as string,
        nom: data.nom as string,
        email: data.email as string,
        telephone: (data.telephone as string) || undefined,
        consentementSilencieux: data.consentementSilencieux as boolean,
        consentementConsultation: data.consentementConsultation as boolean,
        consentementModificationPlanning: data.consentementModificationPlanning as boolean,
      }

      createEtudiant.mutate(payload, {
        onSuccess: () => navigate('/etudiants'),
      })
    }
  }

  if (isEdit && isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  return (
    <div>
      {/* Header: back button + student name (edit) or create title */}
      {isEdit ? (
        <div className="relative mb-4 flex items-center">
          <button
            onClick={() => navigate('/etudiants')}
            className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('student.list.pageTitle', 'Étudiants')}
          </button>
          {etudiant && (
            <h1 className="absolute inset-0 flex items-center justify-center pointer-events-none text-2xl font-bold text-neutral-900">
              {etudiant.prenom} {etudiant.nom}
            </h1>
          )}
        </div>
      ) : (
        <EntityPageHeader
          title={t('student.identity.createTitle', 'Créer un étudiant')}
          backTo="/etudiants"
        />
      )}

      {isEdit && (
        <EtudiantTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          calendarLabel={calendarLabel}
        />
      )}

      {activeTab === 'identity' && (
        <EtudiantForm
          etudiant={etudiant}
          onSubmit={handleSubmit}
          isPending={createEtudiant.isPending || updateEtudiant.isPending}
        />
      )}

      {activeTab === 'calendar' && isEdit && (
        <CalendrierTab
          etudiantId={parseInt(id!)}
          onAnneeScolaireLoaded={setCalendarLabel}
        />
      )}

      {activeTab === 'schedule' && isEdit && (
        <EmploiDuTempsTab etudiantId={parseInt(id!)} />
      )}

      {activeTab === 'activities' && isEdit && (
        <ActivitesTab etudiantId={parseInt(id!)} />
      )}

      {activeTab === 'planning' && isEdit && (
        <PlanningTab etudiantId={parseInt(id!)} />
      )}
    </div>
  )
}
